import { BehaviorSubject, map, Observable, throwError } from 'rxjs';
import type { NotificationFormData, NotificationPayload, GenerateResponse, GeometryState } from '@/shared/types';
import { authService } from '@/features/auth/services/auth.service';
import { mapService } from '@/features/map/services/map.service';
import { submitNotification, generateNotification } from '../api/notification.api';

function todayDate(): string {
  return new Date().toISOString().split('T')[0];
}

function currentTime(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function addHour(time: string): string {
  const [h, m] = time.split(':').map(Number);
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const INITIAL: NotificationFormData = {
  operatorId: '',
  pilotPhone: '',
  uasId: '',
  operatorRegistrationNumber: '',
  category: '',
  purpose: '',
  flightDate: todayDate(),
  flightTimeFrom: currentTime(),
  flightTimeTo: addHour(currentTime()),
  location: '',
  plannedAltitude: 50,
};

function createNotificationService() {
  const formData$ = new BehaviorSubject<NotificationFormData>(INITIAL);
  const currentStep$ = new BehaviorSubject<1 | 2 | 3 | 4>(1);

  const step1Valid$ = formData$.pipe(
    map(d => !!(d.operatorId && d.pilotPhone && d.uasId && d.operatorRegistrationNumber && d.category))
  );
  const step2Valid$ = formData$.pipe(
    map(d => !!(d.purpose && d.flightDate && d.flightTimeFrom && d.flightTimeTo && d.location))
  );
  const step3Valid$ = mapService.geometry$.pipe(
    map(g => g.takeoffPoint !== null)
  );
  const alwaysValid$ = new BehaviorSubject(true).asObservable();

  function getStepValid$(step: 1 | 2 | 3 | 4): Observable<boolean> {
    if (step === 1) return step1Valid$;
    if (step === 2) return step2Valid$;
    if (step === 3) return step3Valid$;
    return alwaysValid$;
  }

  function buildPayload(geometry: GeometryState): NotificationPayload {
    const data = formData$.value;
    const user = authService.user$.value;

    if (!user) throw new Error('Not authenticated');
    if (!geometry.takeoffPoint) throw new Error('No takeoff point set');

    const operator = user.operators.find(o => o.id === data.operatorId);
    const uas = user.uasList.find(u => u.id === data.uasId);
    return {
      deviceId: data.uasId,
      operatorId: data.operatorId,
      operator: operator?.name ?? data.operatorId,
      pilotPhone: data.pilotPhone,
      uasType: uas?.type ?? data.uasId,
      uasSerialNumber: uas?.serialNumber ?? '',
      operatorRegistrationNumber: operator?.registrationNumber ?? data.operatorRegistrationNumber,
      category: data.category,
      purpose: data.purpose,
      flightDate: data.flightDate,
      flightTimeFrom: data.flightTimeFrom,
      flightTimeTo: data.flightTimeTo,
      location: data.location,
      takeoffPoint: geometry.takeoffPoint,
      plannedAltitude: data.plannedAltitude,
      bufferZoneMeters: mapService.zoneSettings$.value.bufferMeters,
      terminationZoneMeters: mapService.zoneSettings$.value.terminationMeters,
      ...(data.maxDistance ? { maxDistance: data.maxDistance } : {}),
      ...(geometry.flightArea.length >= 3 ? { flightArea: geometry.flightArea } : {}),
      ...(geometry.additionalTakeoffPoints.length > 0
        ? { additionalTakeoffPoints: geometry.additionalTakeoffPoints }
        : {}),
    };
  }

  function submit(geometry: GeometryState): Observable<GenerateResponse> {
    let payload: NotificationPayload;
    try {
      payload = buildPayload(geometry);
    } catch (err) {
      return throwError(() => err);
    }
    return submitNotification(payload);
  }

  // Creates the flight notification and generates its PDF/KML without
  // submitting it — the flight stays a draft and no email is sent to the CAA.
  function generateOnly(geometry: GeometryState): Observable<GenerateResponse> {
    let payload: NotificationPayload;
    try {
      payload = buildPayload(geometry);
    } catch (err) {
      return throwError(() => err);
    }
    return generateNotification(payload);
  }

  return {
    formData$,
    currentStep$,
    updateFormData: (partial: Partial<NotificationFormData>) =>
      formData$.next({ ...formData$.value, ...partial }),
    setStep: (step: 1 | 2 | 3 | 4) => currentStep$.next(step),
    getStepValid$,
    submit,
    generateOnly,
    reset: () => {
      formData$.next({ ...INITIAL, flightDate: todayDate(), flightTimeFrom: currentTime(), flightTimeTo: addHour(currentTime()) });
      currentStep$.next(1);
    },
  };
}

export const notificationService = createNotificationService();
